import React, { useMemo } from "react";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import { resolveProviderBranding } from "./resolveProviderBranding";
import IdpIcon from "./IdpIcon";

/**
 * Provider-aware SSO login button with tenant branding overrides.
 *
 * @param {object} props
 * @param {string} [props.provider]
 * @param {'oidc' | 'saml'} [props.protocol]
 * @param {string} [props.displayName]
 * @param {string | null} [props.buttonLabel]
 * @param {string | null} [props.logoUrl]
 * @param {string | null} [props.brandColor]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.disabled]
 * @param {() => void} props.onClick
 * @param {string} [props.testId]
 */
export default function SSOLoginButton({
  provider,
  protocol = "oidc",
  displayName,
  buttonLabel = null,
  logoUrl = null,
  brandColor = null,
  loading = false,
  disabled = false,
  onClick,
  testId,
}) {
  const branding = useMemo(
    () =>
      resolveProviderBranding({
        provider,
        protocol,
        displayName,
        buttonLabel,
        logoUrl,
        brandColor,
      }),
    [provider, protocol, displayName, buttonLabel, logoUrl, brandColor]
  );

  const isDisabled = disabled || loading;
  const useBrandedSurface = Boolean(branding.buttonBackground);
  const label = loading ? branding.loadingMessage : branding.buttonLabel;
  const ariaLabel = loading ? branding.loadingMessage : branding.ariaLabel;

  const buttonSx = useMemo(() => {
    const base = {
      minHeight: 44,
      justifyContent: "center",
      gap: 1.25,
      "&:focus-visible": {
        outline: "2px solid #84D2EF",
        outlineOffset: 2,
      },
    };

    if (!useBrandedSurface) {
      return base;
    }

    return {
      ...base,
      backgroundColor: branding.buttonBackground,
      color: branding.buttonTextColor,
      border: "1px solid rgba(255, 255, 255, 0.16)",
      "&:hover": {
        backgroundColor: branding.buttonBackground,
        filter: "brightness(0.95)",
      },
      "&.Mui-disabled": {
        backgroundColor: branding.buttonBackground,
        color: branding.buttonTextColor,
        opacity: 0.72,
      },
    };
  }, [branding.buttonBackground, branding.buttonTextColor, useBrandedSurface]);

  return (
    <Button
      type="button"
      variant="contained"
      color={useBrandedSurface ? "inherit" : "primary"}
      fullWidth
      className="button sso-login-button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      data-testid={testId}
      sx={buttonSx}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
          width: "100%",
        }}
      >
        {loading ? (
          <CircularProgress
            size={18}
            color="inherit"
            aria-hidden="true"
            data-testid="sso-button-spinner"
          />
        ) : (
          <IdpIcon
            iconKey={branding.iconKey}
            logoUrl={branding.logoUrl}
            alt=""
          />
        )}
        <Typography component="span" variant="inherit">
          {label}
        </Typography>
      </Box>
    </Button>
  );
}
